import { useRef, useState } from "react";

import { Autocomplete, Button, TextField, Typography } from "@mui/material";
import classNames from "classnames";
import { IoSync } from "react-icons/io5";

import { apis, callApi } from "../../../../../api";
import { defaultTimezone } from "../../../../../constants/timezone";
import { useAccount } from "../../../../../hooks/useAccount";
import { GoogleMapsService } from "../../../../../services/GoogleMapsService";
import { GoogleMaps } from "../../../../common/GoogleMaps/GoogleMaps";
import { useMyCourseInstructor } from "../../hooks/useMyCourseInstructor";

export function MyCourseLocation() {
	const { course, updateCourseBasicInformation } = useMyCourseInstructor();
	const [inputValue, setInputValue] = useState(course?.location ?? "");
	const [location, setLocation] = useState(course?.location ?? null);
	const [latitude, setLatitude] = useState(course?.latitude ?? null);
	const [longitude, setLongitude] = useState(course?.longitude ?? null);
	const [placePredictions, setPlacePredictions] = useState<
		google.maps.places.QueryAutocompletePrediction[]
	>([]);
	const [loading, setLoading] = useState(false);
	const { account } = useAccount();

	const debounceRef = useRef<NodeJS.Timeout | null>(null);

	if (!course) {
		return null;
	}

	const getLocation = async (address: string) => {
		setPlacePredictions([]);
		if (address.length === 0) {
			return;
		}
		const fakeMap = document.getElementById("fake-map");
		if (!fakeMap) {
			return;
		}
		const { PlacesService } = await GoogleMapsService.loadPlaces();
		const placeService = new PlacesService(fakeMap as HTMLDivElement);
		placeService.textSearch({ query: address }, (result) => {
			if (!result || result.length === 0) {
				return;
			}

			const found = result[0];
			const lat = found.geometry?.location?.lat() ?? 0;
			const lng = found.geometry?.location?.lng() ?? 0;
			setLocation(address);
			setLatitude(lat);
			setLongitude(lng);
		});
	};

	const onMarkerPositionChange = (lat: number, lng: number) => {
		setLatitude(lat);
		setLongitude(lng);
	};

	const getPlacePredictions = (text: string) => {
		if (debounceRef.current) {
			setPlacePredictions([]);
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}

		if (text.trim() === "") {
			setPlacePredictions([]);
			return;
		}

		debounceRef.current = setTimeout(async () => {
			const { AutocompleteService } = await GoogleMapsService.loadPlaces();
			const autocompleteService = new AutocompleteService();
			autocompleteService.getQueryPredictions({ input: text }, (result) => {
				setPlacePredictions(result ?? []);
			});
		}, 500);
	};

	const onClickCancel = () => {
		setLocation(course.location);
		setLatitude(course.latitude);
		setLongitude(course.longitude);
	};

	const onClickUpdate = async () => {
		if (isDisabled) {
			return;
		}

		setLoading(true);
		let timezoneName: string | undefined = undefined;
		if (location !== null) {
			const response = await callApi({
				method: "GET",
				url: `${
					apis.GOOGLE_MAP_TIME_ZONE
				}?location=${latitude} ${longitude}&timestamp=${Math.round(
					Date.now() / 1000,
				)}&key=${process.env.REACT_APP_TIME_ZONE_API_KEY}`,
				baseURL: "",
			});
			if (response) {
				timezoneName = response.timeZoneId;
			}
		} else {
			timezoneName = account?.timezoneName ?? defaultTimezone;
		}

		await updateCourseBasicInformation({
			location,
			latitude,
			longitude,
			timezoneName,
		});
		setLoading(false);
	};

	const isEdited =
		(location !== course.location ||
			latitude !== course.latitude ||
			longitude !== course.longitude) &&
		!loading;

	const isDisabled = !isEdited || loading;

	return (
		<>
			<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
				<Typography className="small-title-text bold">
					2. Where's your service taking place?
				</Typography>
				{loading && (
					<div>
						<IoSync className="normal-icon rotation-icon blue-icon" />
					</div>
				)}
			</div>
			<Typography className="normal-text">
				Enter address where you meet or general area if you travel to multiple
				locations or leave blank if it's all online.
			</Typography>
			<div className="common-autocomplete">
				<Autocomplete
					inputValue={inputValue}
					onInputChange={(e, newInputValue, reason) => {
						if (reason === "reset") {
							return;
						}
						if (newInputValue.length > 0) {
							setInputValue(newInputValue);
							getPlacePredictions(newInputValue);
						} else {
							setInputValue("");
							setLocation(null);
							setLatitude(null);
							setLongitude(null);
						}
					}}
					onOpen={() => {
						setPlacePredictions([]);
					}}
					onClose={(e, reason) => {
						if (reason === "blur") {
							if (location !== inputValue) {
								getLocation(inputValue);
							}
						}
					}}
					onChange={(e, value) => {
						if (value?.description) {
							setInputValue(value.description);
							getLocation(value.description);
						} else {
							setInputValue("");
							setLocation(null);
							setLatitude(null);
							setLongitude(null);
						}
					}}
					options={placePredictions}
					isOptionEqualToValue={(option1, option2) =>
						option1.place_id === option2.place_id
					}
					filterOptions={(options) => options}
					getOptionLabel={(option) => option.description ?? ""}
					className="input-field-inner item-separator-small"
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder="Your service location"
							multiline
							slotProps={{
								input: {
									...params.InputProps,
									className: "normal-text",
									classes: {
										input: "input-field-inner",
									},
									onKeyDown(e) {
										if (e.key === "Enter") {
											e.preventDefault();
										}
									},
								},
							}}
						/>
					)}
				/>
			</div>
			<div id="fake-map" />
			{location !== null && latitude !== null && longitude !== null && (
				<>
					<Typography className="normal-text item-separator-big">
						Drag map to reposition the pin in the right spot
					</Typography>
					<GoogleMaps
						lat={latitude}
						lng={longitude}
						className="map item-separator-small"
						markerType="centering"
						onMarkerPositionChange={onMarkerPositionChange}
					/>
				</>
			)}
			<div className="button-container item-separator-small">
				<Button
					className="normal-button disabled-button"
					disabled={!isEdited}
					onClick={onClickCancel}>
					Cancel
				</Button>
				<Button
					disabled={isDisabled}
					className={classNames("normal-button", {
						"primary-button": !isDisabled,
						"disabled-button": isDisabled,
					})}
					onClick={onClickUpdate}>
					Save
				</Button>
			</div>
		</>
	);
}
